import { useEffect, useState } from "react";
import { useIsVerified } from "src/components/email-verification";
import {
  MainCard,
  MainCardContent,
  MainCardTitle,
} from "src/components/main-card-type.tsx";
import { Button } from "src/components/ui/button";
import {
  checkCalculatorUsageLimitAndRedirect,
  loadSavedCalculatorData,
  sanitizeBrlValue,
  saveCalculatorData,
} from "src/utils/functions";
import {
  CONSTANTES_2025,
  CustoFuncionarioInputData,
  CustoFuncionarioResultsType,
  EXPLICACOES_CAMPOS,
  ValidationMessages,
} from "../calculadora-custo-funcionario";
import { calcularCustoFuncionario } from "../utils/custoFuncionarioFunctions";

const CALCULATOR_KEY = "calculadora-custo-funcionario-clt";

const DEFAULT_VALUES: CustoFuncionarioInputData = {
  salarioBruto: "",
  valeTransporte: "",
  valeAlimentacao: "",
  planoSaude: "",
  outrosBeneficios: "",
  regimeEmpresa: "simples",
} as const;

const Tooltip = ({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) => (
  <div className="relative group">
    {children}
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 w-64 text-xs text-white bg-gray-800 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20 text-center">
      {text}
    </div>
  </div>
);

const parseNumericValue = (value: string): number => {
  return (
    Number.parseFloat(value.replace(/[^0-9,-]+/g, "").replace(",", ".")) || 0
  );
};

export const CustoFuncionarioInputCard = ({
  setResults,
}: {
  setResults: (value: CustoFuncionarioResultsType | null) => void;
}) => {
  const [message, setMessage] = useState<string>("");
  const [formData, setFormData] =
    useState<CustoFuncionarioInputData>(DEFAULT_VALUES);
  const [isCalculating, setIsCalculating] = useState(false);

  const isVerified = useIsVerified();

  const updateFormField = <K extends keyof CustoFuncionarioInputData>(
    field: K,
    value: CustoFuncionarioInputData[K],
  ) => {
    setFormData((prev: CustoFuncionarioInputData) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateSalario = (salario: string): string | null => {
    if (!salario) return ValidationMessages.SALARIO_OBRIGATORIO;

    const salarioNum = parseNumericValue(salario);
    if (salarioNum <= 0) return ValidationMessages.SALARIO_INVALIDO;

    if (salarioNum < CONSTANTES_2025.SALARIO_MINIMO) {
      return ValidationMessages.SALARIO_MINIMO;
    }

    return null;
  };

  const validateValeTransporte = (valeTransporte: string): string | null => {
    if (valeTransporte) {
      const valeTransporteNum = parseNumericValue(valeTransporte);
      if (valeTransporteNum < 0) {
        return "Vale transporte não pode ser negativo.";
      }
    }
    return null;
  };

  const validateForm = (): string | null => {
    return (
      validateSalario(formData.salarioBruto) ||
      validateValeTransporte(formData.valeTransporte)
    );
  };

  const executeCalculation = async () => {
    try {
      const resultado = calcularCustoFuncionario(
        formData.salarioBruto,
        formData.valeTransporte,
        formData.valeAlimentacao,
        formData.planoSaude,
        formData.outrosBeneficios,
        formData.regimeEmpresa,
      );

      setResults(resultado);
      setMessage("");
    } catch (error) {
      console.error("Erro no executeCalculation:", error);
      setMessage("Erro no cálculo: " + (error as Error).message);
      setResults(null);
    }
  };

  const calcular = async () => {
    const validationError = validateForm();
    if (validationError) {
      setMessage(validationError);
      return;
    }

    setIsCalculating(true);
    setMessage("");

    try {
      await executeCalculation();
    } catch (error) {
      console.error("Erro no cálculo:", error);
      setMessage((error as Error).message || ValidationMessages.ERRO_CALCULO);
      setResults(null);
    } finally {
      setIsCalculating(false);
    }
  };

  const clear = () => {
    setFormData(DEFAULT_VALUES);
    setResults(null);
    setMessage("");
  };

  const handleSave = () => {
    saveCalculatorData(CALCULATOR_KEY, {
      salarioBruto: formData.salarioBruto,
      valeTransporte: formData.valeTransporte,
      valeAlimentacao: formData.valeAlimentacao,
      planoSaude: formData.planoSaude,
      outrosBeneficios: formData.outrosBeneficios,
      regimeEmpresa: formData.regimeEmpresa,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
    checkCalculatorUsageLimitAndRedirect(calcular);
  };

  useEffect(() => {
    const savedData = loadSavedCalculatorData(CALCULATOR_KEY);
    if (savedData) {
      setFormData({
        salarioBruto:
          (savedData.salarioBruto as string) || DEFAULT_VALUES.salarioBruto,
        valeTransporte:
          (savedData.valeTransporte as string) || DEFAULT_VALUES.valeTransporte,
        valeAlimentacao:
          (savedData.valeAlimentacao as string) ||
          DEFAULT_VALUES.valeAlimentacao,
        planoSaude:
          (savedData.planoSaude as string) || DEFAULT_VALUES.planoSaude,
        outrosBeneficios:
          (savedData.outrosBeneficios as string) ||
          DEFAULT_VALUES.outrosBeneficios,
        regimeEmpresa:
          (savedData.regimeEmpresa as "simples" | "lucro") ||
          DEFAULT_VALUES.regimeEmpresa,
      });
    }
  }, []);

  return (
    <MainCard>
      <MainCardTitle>
        Calculadora de Custo de Funcionário CLT para Empresas
      </MainCardTitle>
      <form onSubmit={handleSubmit}>
        <MainCardContent>
          {/* Salário Bruto */}
          <div className="px-4">
            <Tooltip text={EXPLICACOES_CAMPOS.salarioBruto}>
              <label
                className="block mb-2 font-medium text-sm cursor-help"
                htmlFor="salario-bruto"
              >
                Salário Bruto CLT * ℹ️
              </label>
            </Tooltip>
            <div className="flex border">
              <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                R$
              </span>
              <input
                className="flex-1 p-2 min-w-[120px]"
                id="salario-bruto"
                placeholder="0,00"
                type="text"
                value={formData.salarioBruto}
                onChange={(event) =>
                  updateFormField("salarioBruto", sanitizeBrlValue(event))
                }
              />
            </div>
          </div>
        </MainCardContent>

        {/* Grid com Benefícios e Regime */}
        <div className="px-4 pb-4 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Benefícios */}
            <div className="bg-[#e9ecef] p-4 rounded border">
              <h3 className="text-sm font-semibold mb-3 text-gray-700">
                Benefícios
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Vale Transporte */}
                <div>
                  <Tooltip text={EXPLICACOES_CAMPOS.valeTransporte}>
                    <label
                      className="block mb-2 font-medium text-sm cursor-help"
                      htmlFor="vale-transporte"
                    >
                      Vale Transporte ℹ️
                    </label>
                  </Tooltip>
                  <div className="flex border">
                    <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                      R$
                    </span>
                    <input
                      className="flex-1 p-2 min-w-[100px]"
                      id="vale-transporte"
                      placeholder="0,00"
                      type="text"
                      value={formData.valeTransporte}
                      onChange={(event) =>
                        updateFormField(
                          "valeTransporte",
                          sanitizeBrlValue(event),
                        )
                      }
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Máx. 6% salário</p>
                </div>

                {/* Vale Alimentação */}
                <div>
                  <label
                    className="block mb-2 font-medium text-sm"
                    htmlFor="vale-alimentacao"
                  >
                    Vale Alimentação
                  </label>
                  <div className="flex border">
                    <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                      R$
                    </span>
                    <input
                      className="flex-1 p-2 min-w-[100px]"
                      id="vale-alimentacao"
                      placeholder="0,00"
                      type="text"
                      value={formData.valeAlimentacao}
                      onChange={(event) =>
                        updateFormField(
                          "valeAlimentacao",
                          sanitizeBrlValue(event),
                        )
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Plano de Saúde */}
                <div>
                  <label
                    className="block mb-2 font-medium text-sm"
                    htmlFor="plano-saude"
                  >
                    Plano de Saúde
                  </label>
                  <div className="flex border">
                    <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                      R$
                    </span>
                    <input
                      className="flex-1 p-2 min-w-[100px]"
                      id="plano-saude"
                      placeholder="0,00"
                      type="text"
                      value={formData.planoSaude}
                      onChange={(event) =>
                        updateFormField("planoSaude", sanitizeBrlValue(event))
                      }
                    />
                  </div>
                </div>

                {/* Outros Benefícios */}
                <div>
                  <label
                    className="block mb-2 font-medium text-sm"
                    htmlFor="outros-beneficios"
                  >
                    Outros Benefícios
                  </label>
                  <div className="flex border">
                    <span className="inline-flex items-center px-3 border-r text-sm bg-[#e9ecef]">
                      R$
                    </span>
                    <input
                      className="flex-1 p-2 min-w-[100px]"
                      id="outros-beneficios"
                      placeholder="0,00"
                      type="text"
                      value={formData.outrosBeneficios}
                      onChange={(event) =>
                        updateFormField(
                          "outrosBeneficios",
                          sanitizeBrlValue(event),
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Regime Tributário */}
            <div className="bg-[#e9ecef] p-4 rounded border">
              <Tooltip text={EXPLICACOES_CAMPOS.regimeEmpresa}>
                <h3 className="text-sm font-semibold mb-3 text-gray-700 cursor-help">
                  Regime Tributário da Empresa * ℹ️
                </h3>
              </Tooltip>

              <div className="space-y-3">
                <label className="flex items-start gap-3 p-3 bg-white rounded border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="regimeEmpresa"
                    value="simples"
                    checked={formData.regimeEmpresa === "simples"}
                    onChange={(e) =>
                      updateFormField(
                        "regimeEmpresa",
                        e.target.value as "simples",
                      )
                    }
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ accentColor: "#8d0202" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="mb-1">
                      <span className="font-medium text-sm text-gray-900">
                        Simples Nacional
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div>• Faturamento até R$ 4,8 milhões/ano</div>
                      <div>
                        • <strong>Encargos:</strong> 28% (INSS 20% + FGTS 8%)
                      </div>
                      <div>
                        • <strong>Isenções:</strong> INSS 20%, Salário Educação
                        2,5%, Sistema S 3,3%, Seguro 2%
                      </div>
                    </div>
                  </div>
                </label>

                <label className="flex items-start gap-3 p-3 bg-white rounded border border-gray-200 hover:border-gray-300 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="regimeEmpresa"
                    value="lucro"
                    checked={formData.regimeEmpresa === "lucro"}
                    onChange={(e) =>
                      updateFormField(
                        "regimeEmpresa",
                        e.target.value as "lucro",
                      )
                    }
                    className="w-4 h-4 mt-0.5 flex-shrink-0"
                    style={{ accentColor: "#8d0202" }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="mb-1">
                      <span className="font-medium text-sm text-gray-900">
                        Lucro Presumido/Real
                      </span>
                    </div>
                    <div className="text-xs text-gray-600 space-y-0.5">
                      <div>• Sem limite de faturamento</div>
                      <div>
                        • <strong>Encargos:</strong> 35,8% total da folha
                      </div>
                      <div>
                        • INSS 20% + Salário Educação 2,5% + Sistema S 3,3%
                      </div>
                      <div>• Seguro Acidente 2% + FGTS 8%</div>
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Container isolado para os botões - CORREÇÃO APLICADA */}
        <div className="px-4 pb-4">
          <div className="flex justify-between">
            <Button
              disabled={!isVerified || isCalculating}
              type="submit"
              variant="default"
              className="sm:w-[150px]"
            >
              {isCalculating ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  Calculando...
                </>
              ) : (
                "Calcular"
              )}
            </Button>

            <Button onClick={clear} variant="ghost" type="button">
              Limpar
            </Button>
          </div>
        </div>
      </form>

      <p className="text-red-800 text-center mt-2 font-bold">{message}</p>
    </MainCard>
  );
};
